import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

// Map visualization props
export interface MapVisualizationProps {
  /**
   * Data to display on the map
   * Each item should have a location (id, name, lat, lng) and a value
   */
  data: Array<{
    locationId: string;
    locationName: string;
    latitude: number;
    longitude: number;
    value: number;
  }>;
  /**
   * Width of the map
   */
  width?: number | string;
  /**
   * Height of the map
   */
  height?: number | string;
  /**
   * Color scale for the data points
   */
  colorScale?: string[];
  /**
   * Callback when a location is clicked
   */
  onLocationClick?: (locationId: string) => void;
  /**
   * Additional class name
   */
  className?: string;
}

// Styled components
const MapContainer = styled.div<{ $width: number | string; $height: number | string }>`
  width: ${props => typeof props.$width === 'number' ? `${props.$width}px` : props.$width};
  height: ${props => typeof props.$height === 'number' ? `${props.$height}px` : props.$height};
  position: relative;
  overflow: hidden;
  background-color: var(--background-secondary, #f5f5f5);
  border-radius: 4px;
`;

const MapSvg = styled.svg`
  width: 100%;
  height: 100%;
`;

const MapLocation = styled.circle<{ $value: number; $max: number; $colorScale: string[] }>`
  fill: ${props => {
    const normalizedValue = props.$max > 0 ? props.$value / props.$max : 0;
    const colorIndex = Math.min(
      Math.floor(normalizedValue * props.$colorScale.length),
      props.$colorScale.length - 1
    );
    return props.$colorScale[colorIndex];
  }};
  stroke: var(--background-primary, #ffffff);
  stroke-width: 1;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  
  &:hover {
    stroke-width: 2;
    transform: scale(1.2);
  }
`;

const MapTooltip = styled.div<{ $x: number; $y: number }>`
  position: absolute;
  left: ${props => props.$x}px;
  top: ${props => props.$y}px;
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 8px;
  border-radius: 4px;
  font-size: 12px;
  pointer-events: none;
  transform: translate(-50%, -100%);
  margin-top: -8px;
  z-index: 10;
  
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 100%;
    transform: translateX(-50%);
    border-width: 5px;
    border-style: solid;
    border-color: rgba(0, 0, 0, 0.8) transparent transparent transparent;
  }
`;

/**
 * MapVisualization component for displaying data on a map
 */
const MapVisualization: React.FC<MapVisualizationProps> = ({
  data,
  width = '100%',
  height = 400,
  colorScale = ['#e6f7ff', '#91d5ff', '#40a9ff', '#1890ff', '#096dd9'],
  onLocationClick,
  className,
}) => {
  const [tooltip, setTooltip] = useState<{
    visible: boolean;
    x: number;
    y: number;
    locationName: string;
    value: number;
  }>({
    visible: false,
    x: 0,
    y: 0,
    locationName: '',
    value: 0,
  });
  
  const [mapDimensions, setMapDimensions] = useState({ width: 800, height: 400 });
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);
  
  // Calculate the maximum value for scaling
  const maxValue = Math.max(...data.map(item => item.value), 1);
  
  // Update map dimensions when container size changes
  useEffect(() => {
    if (!containerRef) return;
    
    const updateDimensions = () => {
      if (containerRef) {
        setMapDimensions({
          width: containerRef.clientWidth,
          height: containerRef.clientHeight,
        });
      }
    };
    
    // Initial update
    updateDimensions();
    
    // Add resize listener
    window.addEventListener('resize', updateDimensions);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', updateDimensions);
    };
  }, [containerRef]);
  
  // Simple Mercator projection
  const projectPoint = (lat: number, lng: number) => {
    // Convert latitude and longitude to x, y coordinates
    const x = (lng + 180) * (mapDimensions.width / 360);
    const latRad = (lat * Math.PI) / 180;
    const mercN = Math.log(Math.tan((Math.PI / 4) + (latRad / 2)));
    const y = (mapDimensions.height / 2) - (mapDimensions.width * mercN / (2 * Math.PI));
    
    return { x, y };
  };
  
  const handleMouseEnter = (e: React.MouseEvent, item: typeof data[0]) => {
    const rect = containerRef?.getBoundingClientRect();
    if (!rect) return;
    
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    setTooltip({
      visible: true,
      x,
      y,
      locationName: item.locationName,
      value: item.value,
    });
  };
  
  const handleMouseLeave = () => {
    setTooltip(prev => ({ ...prev, visible: false }));
  };
  
  const handleClick = (locationId: string) => {
    if (onLocationClick) {
      onLocationClick(locationId);
    }
  };
  
  return (
    <MapContainer 
      $width={width} 
      $height={height} 
      className={className}
      ref={setContainerRef}
    >
      <MapSvg viewBox={`0 0 ${mapDimensions.width} ${mapDimensions.height}`}>
        {data.map(item => {
          const { x, y } = projectPoint(item.latitude, item.longitude);
          const radius = Math.max(5, Math.min(10, 5 + (item.value / maxValue) * 10));
          
          return (
            <MapLocation
              key={item.locationId}
              cx={x}
              cy={y}
              r={radius}
              $value={item.value}
              $max={maxValue}
              $colorScale={colorScale}
              onMouseEnter={(e) => handleMouseEnter(e, item)}
              onMouseLeave={handleMouseLeave}
              onClick={() => handleClick(item.locationId)}
              aria-label={`${item.locationName}: ${item.value}`}
            />
          );
        })}
      </MapSvg>
      
      {tooltip.visible && (
        <MapTooltip $x={tooltip.x} $y={tooltip.y}>
          <div>{tooltip.locationName}</div>
          <div><strong>{tooltip.value}</strong></div>
        </MapTooltip>
      )}
    </MapContainer>
  );
};

export default MapVisualization;